'use client'

import { useCursorPrefetchRegistry } from '@/context/CursorPrefetchContext'
import { useUAContext } from '@/context/UAContext'

const VIEWPORT_MARGIN = '200px'
// Filter cards the user scrolls past quickly on mobile.
const VIEWPORT_DWELL_MS = 150

/**
 * Prefetch strategy split by pointer type:
 *
 * Desktop (fine pointer): cursor proximity via CursorPrefetchContext — prefetch
 * when the cursor moves within a dynamic radius of the card center. One global
 * mousemove listener shared across all cards.
 *
 * Mobile (coarse pointer): IntersectionObserver with 200px margin and 150ms
 * dwell filter. Skips cards that fly past during fast scrolling.
 *
 * One-shot: fires once per mount, then disconnects.
 * Returns a ref callback to attach to the card element.
 */
export function useCardPrefetchRef(onNear: () => void) {
  const registry = useCursorPrefetchRegistry()
  const { isCoarsePointer: isCoarse } = useUAContext()

  return (el: HTMLElement | null) => {
    if (!el) return

    // Desktop without provider: no-op — avoid falling through to mobile path.
    if (!isCoarse && !registry) return

    // Desktop: register with shared cursor proximity registry.
    if (!isCoarse) {
      const key = Symbol()
      registry!.register(key, { el, onNear })
      return () => registry!.unregister(key)
    }

    // Mobile: IntersectionObserver with dwell filter.
    let disconnected = false
    let dwellTimer: ReturnType<typeof setTimeout> | null = null

    const clearDwell = () => {
      if (dwellTimer === null) return
      clearTimeout(dwellTimer)
      dwellTimer = null
    }

    const disconnect = () => {
      if (disconnected) return
      disconnected = true
      clearDwell()
      observer.disconnect()
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          clearDwell()
          return
        }
        if (dwellTimer !== null) return
        dwellTimer = setTimeout(() => {
          dwellTimer = null
          onNear()
          disconnect()
        }, VIEWPORT_DWELL_MS)
      },
      { rootMargin: VIEWPORT_MARGIN },
    )

    observer.observe(el)
    return disconnect
  }
}
